import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2, AlertCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function Status() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/status/${token}`)
      .then((r) => mounted && setData(r.data))
      .catch((e) => {
        if (!mounted) return;
        setError(
          e?.response?.status === 404
            ? "We couldn’t find an application for this link."
            : "Something went wrong. Please try again in a moment."
        );
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  if (error) {
    return (
      <div
        data-testid="status-error"
        className="bg-bone min-h-screen flex items-center"
      >
        <div className="tr-container grid grid-cols-12 gap-10 py-20">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-vermilion mb-6 flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5" /> Unknown link
            </div>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight">
              {error}
            </h1>
            <p className="mt-6 text-ink/70 text-lg max-w-xl">
              If you typed the link by hand, please check it again. If it was
              shared with you, please write to us and we’ll track it down.
            </p>
            <div className="mt-10 flex gap-4 flex-wrap">
              <Link to="/careers" data-testid="status-error-careers" className="btn-primary">
                See open roles <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <a
                href="mailto:contact@thinkright-tech.com"
                data-testid="status-error-email"
                className="btn-outline"
              >
                Email us
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        data-testid="status-loading"
        className="bg-bone min-h-screen flex items-center justify-center"
      >
        <div className="overline text-ink/40">Loading your application…</div>
      </div>
    );
  }

  const isRejected = data.stage === "rejected";
  const isHired = data.stage === "hired";

  return (
    <div data-testid="status-page" className="bg-bone min-h-screen">
      {/* Slim top-bar (no main header to keep page self-contained / shareable) */}
      <div className="border-b border-border">
        <div className="tr-container h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-serif text-[22px]">ThinkRight</span>
            <span className="text-vermilion text-[22px] font-serif">.</span>
            <span className="overline text-ink/60 ml-1">Tech</span>
          </Link>
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-ink inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-20 md:pt-28 pb-16">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-vermilion mb-6">
              Application status · {data.role_title}
              {data.role_level ? ` · ${data.role_level}` : ""}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[80px] leading-[0.98] tracking-tight">
              Hello, {data.first_name}.
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-ink/70 font-serif italic leading-relaxed max-w-3xl">
              Here’s where your application stands today — honestly and in plain
              language.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink/60">
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" />
                Applied {fmtDate(data.applied_at)}
              </div>
              {data.updated_at && data.updated_at !== data.applied_at && (
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  Last updated {fmtDate(data.updated_at)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Current stage callout */}
      <section className="pb-12">
        <div className="tr-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`border-t border-b border-border py-10 md:py-14 grid grid-cols-12 gap-6 items-center ${
              isHired ? "bg-vermilion text-white" : ""
            } ${isRejected ? "bg-muted/70" : ""}`}
          >
            <div className="col-span-12 md:col-span-5">
              <div
                className={`overline mb-3 ${
                  isHired ? "text-white" : "text-vermilion"
                }`}
              >
                Current stage
              </div>
              <div
                className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
                data-testid="status-stage-label"
              >
                {data.stage_label}
              </div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p
                className={`text-lg md:text-xl leading-relaxed ${
                  isHired ? "text-white/90" : "text-ink/75"
                }`}
                data-testid="status-stage-description"
              >
                {data.stage_description}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      {!isRejected && (
        <section className="pb-24 md:pb-32">
          <div className="tr-container">
            <div className="overline text-vermilion mb-8">Pipeline</div>
            <ol
              data-testid="status-timeline"
              className="grid grid-cols-1 md:grid-cols-5 border-t border-border"
            >
              {data.stages.map((s, i) => {
                const state =
                  i < data.stage_index
                    ? "done"
                    : i === data.stage_index
                    ? "current"
                    : "upcoming";
                return (
                  <li
                    key={s.key}
                    data-testid={`timeline-${s.key}-${state}`}
                    className={`relative p-6 md:p-8 border-b border-border ${
                      i < data.stages.length - 1 ? "md:border-r" : ""
                    } ${
                      state === "current"
                        ? "bg-bone"
                        : state === "done"
                        ? "bg-muted/40"
                        : ""
                    }`}
                  >
                    <div
                      className={`font-mono text-xs mb-4 ${
                        state === "current"
                          ? "text-vermilion"
                          : "text-ink/40"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      className={`font-serif text-xl md:text-2xl leading-tight tracking-tight mb-3 ${
                        state === "upcoming" ? "text-ink/40" : "text-ink"
                      }`}
                    >
                      {s.label}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em]">
                      {state === "done" && (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-ink/60" />
                          <span className="text-ink/60">Complete</span>
                        </>
                      )}
                      {state === "current" && (
                        <>
                          <span className="w-2 h-2 rounded-full bg-vermilion inline-block" />
                          <span className="text-vermilion">You are here</span>
                        </>
                      )}
                      {state === "upcoming" && (
                        <span className="text-ink/40">Up next</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="pb-24 md:pb-32 border-t border-border pt-16">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-vermilion mb-4">Questions?</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight">
              Something looks off, or you haven’t heard from us in a while?
            </h3>
            <p className="mt-5 text-ink/70 leading-relaxed max-w-xl">
              Write to our recruiting team directly. A real person reads every
              message — we’ll respond within two working days.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="mailto:contact@thinkright-tech.com?subject=Application%20status%20query"
                data-testid="status-email"
                className="btn-primary"
              >
                Email our team <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <Link to="/careers" data-testid="status-careers" className="btn-outline">
                Browse other roles
              </Link>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-10 md:border-l border-border">
            <div className="overline text-ink/50 mb-4">Your link</div>
            <p className="text-ink/70 leading-relaxed text-sm">
              Keep this page bookmarked — it will always show the latest state
              of your application. We update it as soon as the team moves you to
              the next stage.
            </p>
            <p className="mt-4 font-mono text-xs text-ink/50 break-all">
              /status/{token}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
